import { faCheck, faCopy, faLink } from '@awesome.me/kit-8a0d2fd588/icons/modules/sharp/light';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import * as React from 'react';
import { connect } from 'react-redux';

import { PortalTextCommon, PortalTextImportModals } from '../../Models/Api/strongbox.financialportal';
import { ApplicationState } from '../../Store';
import { GetPortalText } from '../../Store/PortalAssets';
import { CopyToClipboard } from '../../Utils/ClipboardUtils';

type InjectedReduxState = {
    commonText: PortalTextCommon | undefined;
    importModalsText: PortalTextImportModals | undefined;
};

type InjectedActionCreators = {};

type ShowLinkProps = {
    copyLinkOnOpen: boolean;
    sharedLink: string;
    workspaceName: string;
    linkExpiration: string | undefined;
    // When true, omit the bold "Secure link" prompt above the link field.
    hideSecureLinkPrompt?: boolean;
    // When true, render the "Copy link" button inside the link field (instead of the caller placing one in
    // the modal footer). Defaults off so existing callers (V1 LenderLinkModal) are unchanged.
    showCopyButtonInField?: boolean;
};

type Props = ShowLinkProps & InjectedReduxState & InjectedActionCreators;

const ShowLinkComponent: React.FC<Props> = (props): React.ReactElement => {
    const [linkJustCopied, setLinkJustCopied] = React.useState<boolean>(false);

    const hideLinkCopiedMessage = (): void => {
        setLinkJustCopied(false);
    }

    const copyLinkToClipboard = (): void => {
        if (!!props.sharedLink) {
            CopyToClipboard(props.sharedLink);

            setLinkJustCopied(true);
        }
    }

    React.useEffect(() => {
        if (props.copyLinkOnOpen && !!props.sharedLink) {
            copyLinkToClipboard();
        }
        // copyLinkToClipboard can change on frequently when it really isn't changing, so disabling the warning
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [props.copyLinkOnOpen, props.sharedLink]);

    React.useEffect(() => {
        if (linkJustCopied) {
            // The in-field button shows a brief "Copied" confirmation (~1.5s); the legacy below-field message
            // lingers longer.
            const copiedDuration = props.showCopyButtonInField ? 1500 : 5000;
            const timerId = setTimeout(() => hideLinkCopiedMessage(), copiedDuration);
            return (() => {
                clearTimeout(timerId);
            });
        }
    }, [linkJustCopied, props.showCopyButtonInField]);

    const { commonText, importModalsText } = props;

    const linkAnchor = (
        <u><a target={'_blank'} rel={'noopener noreferrer'} href={props.sharedLink} style={{ wordBreak: 'break-all' }}>{props.sharedLink}</a></u>
    );

    return (
        <div>
            <p style={{ fontSize: '16px' }}>{importModalsText?.secureURLWarning || ''}</p>
            {props.hideSecureLinkPrompt !== true && (
                <p style={{ fontSize: '16px', fontWeight: 'bold', marginBottom: '0px' }}>{importModalsText?.secureLink || ''}</p>
            )}
            {props.showCopyButtonInField ? (
                // Design-system link field (matches the prototype's "Link created" modal): the fixed-height
                // cr-input (~36px control height) with a leading link icon, the URL in muted 16px text, and a
                // small secondary Copy link button inside it. The button is cr-btn--small so it fits the field
                // height; its label uses the 14px small paragraph size. paddingRight is tightened to 4px so the
                // button sits close to the field's right edge (the base cr-input padding-inline is wider).
                <div className={'cr-input'} style={{ width: '100%', paddingRight: '4px' }}>
                    <span className={'cr-input__icon'}><FontAwesomeIcon icon={faLink} /></span>
                    <input
                        className={'cr-input__control'}
                        value={props.sharedLink}
                        readOnly
                        aria-label={importModalsText?.secureLink || ''}
                        style={{ color: 'var(--text-color-secondary-default)' }}
                    />
                    <button
                        type={'button'}
                        className={'cr-btn cr-btn--secondary cr-btn--small'}
                        onClick={copyLinkToClipboard}
                        style={{ flexShrink: 0, fontSize: 'var(--text-size-paragraph-small)' }}
                    >
                        <span className={'cr-btn__icon'}><FontAwesomeIcon icon={linkJustCopied ? faCheck : faCopy} /></span>
                        <span className={'cr-btn__label'}>{(linkJustCopied ? commonText?.copied : commonText?.copyLink) || ''}</span>
                    </button>
                </div>
            ) : (
                <table style={{ width: '100%', backgroundColor: '#ddd', borderRadius: '0' }}>
                    <tbody>
                        <tr>
                            <td style={{ padding: '10px' }}>
                                {linkAnchor}
                            </td>
                        </tr>
                    </tbody>
                </table>
            )}
            {/* The in-field copy button shows its own "Copied" confirmation, so the below-field message is only
                rendered for the legacy (footer-button) variant. */}
            {props.showCopyButtonInField !== true && (
                <div style={{ height: 'auto', width: '100%', textAlign: 'right' }}>
                    {(linkJustCopied ? (importModalsText?.copiedToYourClipboard || '') : '')}
                </div>
            )}
        </div>
    );
};

export const ShowLink = connect<InjectedReduxState, InjectedActionCreators, ShowLinkProps, ApplicationState>(
    (appState: ApplicationState) => {
        const result: InjectedReduxState = {
            commonText: GetPortalText<PortalTextCommon>(appState, 'commonText'),
            importModalsText: GetPortalText<PortalTextImportModals>(appState, 'importModals'),
        };
        return result;
    },
    {}
)(ShowLinkComponent);
